/** @jsx jsx */

import { Fragment as _, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { css as _css } from 'emotion';
import { css, jsx } from '@emotion/core';
import { colors } from 'styles/variables';

const { mint } = colors;

const Bubble = ({ data }) => {
  let { element, content } = data;

  const [bubbleCoords, setBubbleCoords] = useState<any>({
    x: 0,
    y: window.innerHeight * 2,
    width: 0,
    height: 0,
  });

  const bubble = useRef<HTMLDivElement>(null);
  const arrow = useRef<SVGPolylineElement>(null);

  const arrowData = useRef({
    width: 20,
    height: 10,
    location: 'bottomLeft',
  });

  useEffect(() => {
    if (!bubble.current || !arrow.current) {
      return;
    }

    const { width, height } = bubble.current.getBoundingClientRect();
    const arrowCoords = arrow.current.getBBox();

    const {
      left: x,
      top: y,
      width: elementWidth,
      height: elementHeight,
    } = element.getBoundingClientRect();

    let bubbleAlign = 'left';
    if (x + width >= window.innerWidth) {
      bubbleAlign = 'right';
      arrowData.current.location = 'bottomRight';
    }

    setBubbleCoords({
      x: x - arrowData.current.width / 2,
      y: y - (height + 15),
      ...(bubbleAlign === 'right' && {
        x: x + elementWidth + 10 - width,
      }),
      width,
      height,
    });
  }, []);

  return createPortal(
    <div
      ref={bubble}
      className="fader_-2s"
      css={css({
        position: 'fixed',
        display: 'flex',
        alignItems: 'center',
        left: 0,
        top: 0,
        maxWidth: 320,
        minHeight: 40,
        padding: '10px 20px',
        transform: `translate3d(${bubbleCoords.x}px, ${bubbleCoords.y}px, 0px)`,
        background: 'transparent',
        color: '#fff',
        fontSize: 12,
        transition: 'opacity 0.2s',
        pointerEvents: 'none',
      })}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: bubbleCoords.width,
          height: bubbleCoords.height,
        }}
      >
        <svg
          viewBox={`0 0 ${bubbleCoords.width + 0} ${bubbleCoords.height + 0}`}
          style={{
            filter: 'drop-shadow(5px 10px 10px rgba(0, 0, 0, 0.2))',
            overflow: 'visible',
          }}
        >
          <g>
            <rect
              width={bubbleCoords.width + 0}
              height={bubbleCoords.height}
              rx={20}
              style={{
                fill: mint,
              }}
            />
            <g
              style={{
                transform: `translateX(${
                  {
                    bottomLeft: 20,
                    bottomRight: bubbleCoords.width - 40,
                  }[arrowData.current.location]
                }px)`,
              }}
            >
              <polyline
                ref={arrow}
                points={`
              0,${bubbleCoords.height} 
              ${arrowData.current.width},${bubbleCoords.height} 
              ${arrowData.current.width / 2},${bubbleCoords.height +
                  arrowData.current.height}
              0,${bubbleCoords.height} 
              `}
                style={{
                  fill: mint,
                }}
              />
            </g>
          </g>
        </svg>
      </div>

      <div
        style={{
          position: 'relative',
        }}
      >
        {content}
      </div>
    </div>,
    document.body
  );
};

const InfoBubbleContainer = () => {
  const [bubbleData, setShowBubbleData] = useState<{} | null>(null);

  useEffect(() => {
    const onMouseOver = e => {
      const { target } = e;
      const data = target.dataset.infoBubble;

      if (!data) {
        return;
      }

      setShowBubbleData({
        element: target,
        content: data,
      });
    };

    const onMouseOut = e => {
      if (!e.target.dataset.infoBubble) {
        return;
      }

      setShowBubbleData(null);
    };

    document.body.addEventListener('mouseover', onMouseOver);
    document.body.addEventListener('mouseout', onMouseOut);

    return () => {
      document.body.removeEventListener('mouseover', onMouseOver);
      document.body.removeEventListener('mouseout', onMouseOut);
    };
  }, []);

  return bubbleData ? <Bubble data={bubbleData} /> : null;
};

export { InfoBubbleContainer, Bubble };
